/**
 * read real time price of
 * - trading pairs
 */

import { getIsFreezeLivePrice } from '@ui/constants/isDebug';
import { Interval } from '@ui/constants/poll';
import { useBackendPairsAll } from '@ui/services/backend/hooks/getPairs/useBackendPairsAll';
import { readPriceStream } from '@ui/services/pyth-benchmark/stream/readPriceStream';
import {
  BenchmarkSupportedBaseSymbol,
  isBenchmarkSupportedBaseSymbol,
} from '@ui/services/pyth-benchmark/types';
import { Nullable } from '@ui/types/utility';
import { objectEntries } from '@ui/utils/objectEntries';
import BigNumber from 'bignumber.js';
import { throttle } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { Bar } from 'trading-view';
import { create } from 'zustand';

import { usePriceMapLatest } from './usePriceLatest';

type PriceRecord = Record<BenchmarkSupportedBaseSymbol, Nullable<BigNumber>>;

type PriceState = {
  priceRecord: PriceRecord | undefined;
  setPrice: (asset: BenchmarkSupportedBaseSymbol, price: BigNumber) => void;
};

export const usePriceLiveStore = create<PriceState>()((set, get) => ({
  priceRecord: undefined,
  setPrice(asset, price) {
    set((state) => {
      let currentRecord = state.priceRecord ?? ({} as PriceRecord);
      currentRecord[asset] = price;
      
return { priceRecord: currentRecord };
    });
  },
}));

export const usePriceLive = (symbol: BenchmarkSupportedBaseSymbol) => {
  return usePriceLiveStore((s) => s.priceRecord?.[symbol]);
};

export const useRegisterPriceLiveStore = () => {
  const { data: pairs } = useBackendPairsAll();
  const tickers = useMemo(
    () =>
      Object.values(pairs ?? {}).map((pair) => ({
        symbol: pair.baseSymbol,
        ticker: pair.ticker,
      })),
    [pairs],
  );
  useEffect(() => {
    const isFreezeLivePrice = getIsFreezeLivePrice();
    const store = usePriceLiveStore.getState();
    const unsubs = tickers.map((s) => {
      const throttledOnRead = throttle((data: Bar) => {
        const price = store.priceRecord?.[s.symbol];
        if (isFreezeLivePrice && price) {
          unsub?.();

          return;
        }
        store.setPrice(s.symbol, new BigNumber(data.close));
      }, Interval.FASTEST);

      const unsub = readPriceStream({
        ticker: s.ticker,
        id: 'useRegisterPriceLiveStore-' + s.symbol,
        onRead: throttledOnRead,
      });

      return unsub;
    });

    return () => {
      unsubs.forEach((unsub) => {
        unsub();
      });
    };
  }, [tickers]);
};

export const useInitPriceLiveStore = () => {
  const { data: priceMap } = usePriceMapLatest();
  useEffect(() => {
    if (!priceMap) return;
    const livePriceStore = usePriceLiveStore.getState();
    const priceMapEntries = objectEntries(priceMap);

    // Find entries in priceMap that are not in livePriceStore or have different values
    const diffEntries = priceMapEntries.filter(([symbol]) => {
      if (!isBenchmarkSupportedBaseSymbol(symbol)) {
        return false;
      }

      const livePrice = livePriceStore.priceRecord?.[symbol];

      // Include if live price is missing
      return !livePrice;
    });

    // Only update prices that missing
    diffEntries.forEach(([symbol]) => {
      const latestPrice = priceMap?.[symbol];
      if (latestPrice && isBenchmarkSupportedBaseSymbol(symbol)) {
        livePriceStore.setPrice(symbol, latestPrice);
      }
    });
  }, [priceMap]);
};
